import React from "react";

import { AddRounded, MenuRounded, RefreshRounded } from "@mui/icons-material";
import { Box, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import useFirebase from "../../../config/context/firebaseContext";
import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { RequestList } from "../../../config/services/_BaseFunctions";
import { Dev } from "../../../config/util/consoleUtil";
import UtilFunctions from "../../../config/util/utilFunctions";

import SelectMain from "../../../components/InputMain/SelectMain";
import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";
import BannerItem from "./components/BannerItem";
import ModalWriteBanner from "./components/ModalWriteBanner";

import {
  BannerItemType,
  BannerScreensAvailableType,
  BannerTypesAvailableType,
} from "../../../types/common";

type FilterScreenType = "all" | BannerScreensAvailableType;
type FilterTypeType = "all" | BannerTypesAvailableType;
type FilterStatusType = "all" | "active" | "inactive" | "archived";

export default function AdminBannerPage() {
  const navigate = useNavigate();
  const { getConfig, forceRefreshConfigs } = useFirebase();
  const { setShowSideMenuDrawer } = useMainState();
  const { pushAlert } = useModal();

  const BannerList = RequestList<BannerItemType>("bannersArchived");

  const listOfActives = UtilFunctions.bannerFiltering(getConfig("banner_list"), []).map(
    (item) => item.id
  );

  const [loading, setLoading] = React.useState(false);

  const [showModalWrite, setShowModalWrite] = React.useState(false);

  const [statusFilter, setStatusFilter] = React.useState<FilterStatusType>("active");
  const [screenFilter, setScreenFilter] = React.useState<FilterScreenType>("all");
  const [typeFilter, setTypeFilter] = React.useState<FilterTypeType>("all");

  const [allBanners, setAllBanners] = React.useState<BannerItemType[]>([]);

  const getAllBanners = async () => {
    try {
      const configBanners = getConfig("banner_list") as BannerItemType[];
      let preList = configBanners;

      const result = await BannerList.runRequest();
      preList = [...preList, ...result.map((item) => ({ ...item, archived: true }))];

      setAllBanners(preList);
    } catch (error: any) {
      console.log(...new Dev("getAllBanners", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter banners arquivados",
      });
    }
  };

  const handleReload = async () => {
    setLoading(true);
    await forceRefreshConfigs();
    await getAllBanners();
    setLoading(false);
  };

  const onCloseModalHandler = (reload?: boolean) => {
    setShowModalWrite(false);
    if (reload) handleReload();
  };

  React.useEffect(() => {
    handleReload();
  }, []);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          title="Banners"
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          rightIcon={<RefreshRounded />}
          onRightAction={handleReload}
        />

        {/* Modal */}

        {showModalWrite && <ModalWriteBanner onClose={onCloseModalHandler} />}

        {/* Banners */}

        <Box mt={4} mb={2}>
          <MainSolidButton
            icon={<AddRounded />}
            text="Criar Banner"
            onClick={() => setShowModalWrite(true)}
          />
        </Box>

        <Box>
          {loading ? (
            [1, 2, 3].map((item) => (
              <Skeleton
                key={item}
                animation="wave"
                variant="rounded"
                width={"100%"}
                height={100}
                sx={{ mb: 2, borderRadius: "15px" }}
              />
            ))
          ) : (
            <>
              <Box mb={2}>
                <MainText size="caption" color="regular">
                  Filtrar por:
                </MainText>
              </Box>

              <Box mb={2} display={"flex"} justifyContent={"center"} alignItems={"center"} gap={2}>
                <SelectMain
                  name="Status"
                  options={[
                    { name: "Todos", value: "all" },
                    { name: "Ativos", value: "active" },
                    { name: "Inativos", value: "inactive" },
                    { name: "Arquivados", value: "archived" },
                  ]}
                  onChange={(value) => setStatusFilter(value as FilterStatusType)}
                  value={statusFilter}
                />

                <SelectMain
                  name="Tela"
                  options={[
                    { name: "Todos", value: "all" },
                    { name: "Bus", value: "bus" },
                    { name: "Menu", value: "menu" },
                    { name: "Spott", value: "spott" },
                    { name: "Rating", value: "rating" },
                  ]}
                  onChange={(value) => setScreenFilter(value as FilterScreenType)}
                  value={screenFilter}
                />

                <SelectMain
                  name="Tipo"
                  options={[
                    { name: "Todos", value: "all" },
                    { name: "Success", value: "success" },
                    { name: "Error", value: "error" },
                    { name: "Warning", value: "warning" },
                    { name: "Info", value: "info" },
                    { name: "Default", value: "default" },
                  ]}
                  onChange={(value) => setTypeFilter(value as FilterTypeType)}
                  value={typeFilter}
                />
              </Box>

              {allBanners.map((item) => {
                if (typeFilter !== "all" && item.type !== typeFilter) {
                  return null;
                }

                if (screenFilter !== "all" && item.screen !== screenFilter) {
                  return null;
                }

                const isActive = listOfActives.includes(item.id);

                if (statusFilter === "active" && !isActive) {
                  return null;
                }

                if (statusFilter === "inactive" && (isActive || item.archived)) {
                  return null;
                }

                if (statusFilter === "archived" && !item?.archived) {
                  return null;
                }

                return <BannerItem item={item} isActive={isActive} handleReload={handleReload} />;
              })}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
